import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";

import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Home from "@/pages/Home";
import Pipeline from "@/pages/Pipeline";
import ServiceExecution from "@/pages/ServiceExecution";
import ServiceJobDetail from "@/pages/ServiceJobDetail";
import FollowUps from "@/pages/FollowUps";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import Accounts from "@/pages/Accounts";
import AccountDetail from "@/pages/AccountDetail";
import Deals from "@/pages/Deals";
import DealDetail from "@/pages/DealDetail";
import Products from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import Reports from "@/pages/Reports";
import Users from "@/pages/Users";
import AuditLogs from "@/pages/AuditLogs";
import AdminActivityFeed from "@/pages/AdminActivityFeed";
import ImportExport from "@/pages/ImportExport";
import AIExtract from "@/pages/AIExtract";
import Settings from "@/pages/Settings";
import LoginHistory from "@/pages/LoginHistory";
import Account from "@/pages/Account";
import Calendar from "@/pages/Calendar";
import Team from "@/pages/Team";
import Leads from "@/pages/Leads";
import Quotations from "@/pages/Quotations";
import QuotationDetail from "@/pages/QuotationDetail";
import Marketing from "@/pages/Marketing";
import AllContacts from "@/pages/AllContacts";
import ContactsDirectory from "@/pages/ContactsDirectory";
import Feedback from "@/pages/Feedback";
import Performance from "@/pages/Performance";
import PerformanceUserDetail from "@/pages/PerformanceUserDetail";
import MilestoneToastProvider from "@/components/MilestoneToastProvider";
import PerformanceNotifProvider from "@/components/PerformanceNotifProvider";

import "@/App.css";

// Route guard — blocks pages the user lacks permission for.
function RequirePerm({ perm, children }) {
  const { user } = useAuth();
  const ok = !perm || (user?.permissions || []).includes(perm);
  return ok ? children : <Navigate to="/" replace />;
}

function AppRouter() {
  const location = useLocation();
  // Synchronous redirect — handle session_id BEFORE any /me check
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <AppShell>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/leads" element={<Leads />} />
                <Route path="/quotations" element={<Quotations />} />
                <Route path="/quotations/:id" element={<QuotationDetail />} />
                <Route path="/marketing" element={<Marketing />} />
                <Route path="/contacts" element={<ContactsDirectory />} />
                <Route path="/all-contacts" element={<AllContacts />} />
                <Route path="/feedback" element={<Feedback />} />
                <Route path="/pipeline" element={<Pipeline />} />
                <Route path="/pipeline/:pipelineType" element={<Pipeline />} />
                <Route path="/service-execution" element={<ServiceExecution />} />
                <Route path="/service-execution/:jobId" element={<ServiceJobDetail />} />
                <Route path="/followups" element={<FollowUps />} />
                <Route path="/clients" element={<Clients />} />
                <Route path="/clients/:id" element={<ClientDetail />} />
                <Route path="/accounts" element={<Accounts />} />
                <Route path="/accounts/:id" element={<AccountDetail />} />
                <Route path="/deals" element={<Deals />} />
                <Route path="/deals/:id" element={<DealDetail />} />
                <Route path="/products" element={<Products />} />
                <Route path="/products/:id" element={<ProductDetail />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/users" element={<Users />} />
                <Route path="/team" element={<Team />} />
                <Route path="/audit-logs" element={<AuditLogs />} />
                <Route path="/admin/activity" element={<AdminActivityFeed />} />
                <Route path="/import-export" element={<ImportExport />} />
                <Route path="/ai-extract" element={<AIExtract />} />
                <Route path="/performance" element={<Performance />} />
                <Route path="/performance/users/:uid" element={<PerformanceUserDetail />} />
                <Route path="/calendar" element={<Calendar />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/login-history" element={<LoginHistory />} />
                <Route path="/account" element={<Account />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </AppShell>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <MilestoneToastProvider />
        <PerformanceNotifProvider />
        <Toaster richColors position="top-center" />
      </BrowserRouter>
    </AuthProvider>
  );
}
